<script lang="ts" setup>
import { adminFortnoxUsersService } from '@/services/admin/admin-fortnox-users-service';
import { entraAuthService } from '@/services/auth/entra-auth-service';
import {
  PermissionType,
  ReviewPermissionType,
  UserRole,
} from 'cesg-shared/dist/models';
import { computed, ref, watch } from 'vue';
import {
  type PermissionModalAction,
  type PermissionModalPermissions,
} from '../permission-modal/PermissionModal.vue';
import UserModalView, {
  type User,
  type UserModalAction,
  type UserModalEmits,
  type UserModalViewProps,
} from './UserModalView.vue';

export type {
  User,
  UserModalAction,
  UserModalEmits,
  UserModalViewProps,
} from './UserModalView.vue';

export type UserModalProps = {
  data: UserModalViewProps['data'] & {
    user: User;
  };
  hide?: UserModalViewProps['hide'];
};

const currentUser = entraAuthService.me.value;

const props = defineProps<UserModalProps>();

const emit = defineEmits<UserModalEmits>();

type SelectedProject = {
  id: string;
  name: string;
  number: string;
  status?: string;
  permissions?: PermissionModalPermissions;
};

const selectedProject = ref<SelectedProject | null>(null);

const editableUser = ref<User>({
  ...props.data.user,
  projects: [...props.data.user.projects],
});

const initialUser = ref<User>({
  ...props.data.user,
  projects: [...props.data.user.projects],
});

const toComparableUser = (user: User) => {
  return {
    id: user.id,
    name: user.name,
    email: user.email,
    phone: user.phone,
    role: user.role,
    projects: user.projects.map((project) => ({
      id: project.id,
      name: project.name,
      number: project.number,
      permissions: project.permissions,
    })),
  };
};

const userSignature = (user: User) => {
  return JSON.stringify(toComparableUser(user));
};

const canSave = computed(() => {
  if (!editableUser.value.name.trim() || !editableUser.value.email.trim()) {
    return false;
  }
  return userSignature(editableUser.value) !== userSignature(initialUser.value);
});

const permissionModalData = computed(() => {
  if (!selectedProject.value) return null;
  const user = { id: editableUser.value.id, name: editableUser.value.name };
  return {
    user,
    excludeProjectIds: editableUser.value.projects
      .filter((p) => p.id !== selectedProject.value!.id)
      .map((p) => p.id),
    project: {
      id: selectedProject.value.id,
      name: selectedProject.value.name,
      number: selectedProject.value.number,
      status: selectedProject.value.status,
    },
    permissions: selectedProject.value.permissions,
  };
});

const onPermissionModalAction = (action: PermissionModalAction) => {
  switch (action.type) {
    case 'close':
    case 'cancel':
      selectedProject.value = null;
      return;
    case 'save': {
      const project = selectedProject.value;
      if (!project) return;
      for (const p of action.projects) {
        const exists = editableUser.value.projects.some((ep) => ep.id === p.id);
        if (exists) {
          editableUser.value = {
            ...editableUser.value,
            projects: editableUser.value.projects.map((ep) =>
              ep.id === p.id ? { ...ep, permissions: action.permissions } : ep,
            ),
          };
        } else {
          editableUser.value = {
            ...editableUser.value,
            projects: [
              ...editableUser.value.projects,
              {
                id: p.id,
                name: p.name,
                number: p.number ?? p.id,
                status: p.status ?? undefined,
                permissions: action.permissions,
              },
            ],
          };
        }
      }
      selectedProject.value = null;
      return;
    }
  }
};

watch(
  () => props.data.user,
  (newUser) => {
    editableUser.value = {
      ...newUser,
      projects: [...newUser.projects],
    };
    initialUser.value = {
      ...newUser,
      projects: [...newUser.projects],
    };
  },
  { deep: true },
);

type UpdateUserBody = Parameters<typeof adminFortnoxUsersService.updateUser>[1];
const toUpdateFormat = (user: Partial<User>): UpdateUserBody => {
  return {
    user: {
      name: user.name,
      email: user.email,
      phone: user.phone?.trim() || null,
      role: user.role as UserRole,
    },
    permissions: user.projects?.map((project) => ({
      projectId: project.id,
      ...project.permissions,
    })),
  } as UpdateUserBody;
};

const getUserDiff = (old: User, current: User) => {
  const diffValue = function <Key extends keyof User>(
    key: Key,
    old: User[Key],
    current: User[Key],
  ) {
    return JSON.stringify(old) === JSON.stringify(current)
      ? {}
      : { [key]: current };
  };

  return {
    ...diffValue('name', old.name, current.name),
    ...diffValue('email', old.email, current.email),
    ...diffValue('phone', old.phone, current.phone),
    ...diffValue('role', old.role, current.role),
    ...diffValue(
      'projects',
      old.projects.map((project) => ({
        id: project.id,
        name: project.name,
        number: project.number,
        permissions: project.permissions,
      })),
      current.projects.map((project) => ({
        id: project.id,
        name: project.name,
        number: project.number,
        permissions: project.permissions,
      })),
    ),
  };
};

const onAction = async (action: UserModalAction) => {
  switch (action.type) {
    case 'close':
      emit('action', { type: 'close' });
      return;
    case 'save':
      if (!canSave.value) {
        return;
      }
      const userDiff = getUserDiff(initialUser.value, editableUser.value);
      const updateUserBody = toUpdateFormat(userDiff);

      await adminFortnoxUsersService.updateUser(
        props.data.user.id,
        updateUserBody,
      );
      emit('action', { type: 'save' });
      return;
    case 'cancel':
      editableUser.value = {
        ...props.data.user,
        projects: [...props.data.user.projects],
      };
      emit('action', { type: 'cancel' });
      return;
    case 'removeProject':
      editableUser.value = {
        ...editableUser.value,
        projects: editableUser.value.projects.filter(
          (project) => project.id !== action.projectId,
        ),
      };
      return;
    case 'requestAddProject':
      selectedProject.value = {
        id: action.project.id,
        name: action.project.name,
        number: action.project.number,
        status: action.project.status ?? undefined,
      };
      return;
    case 'requestEditProjectPermissions': {
      const project = editableUser.value.projects.find(
        (p) => p.id === action.projectId,
      );
      if (!project) return;
      selectedProject.value = {
        id: project.id,
        name: project.name,
        number: project.number,
        status: project.status,
        permissions: {
          overview: project.permissions.overview as PermissionType,
          budget: project.permissions.budget as PermissionType,
          forecast: project.permissions.forecast as PermissionType,
          ata: project.permissions.ata as PermissionType,
          invoice: project.permissions.invoice as PermissionType,
          files: project.permissions.files as PermissionType,
          review: project.permissions.review as ReviewPermissionType,
        },
      };
      return;
    }
    case 'permissionModalAction':
      onPermissionModalAction(action.action);
      return;
  }
};
</script>

<template>
  <UserModalView
    v-model:user="editableUser"
    :data="props.data"
    :hide="{
      ...props.hide,
      projects: editableUser.role === UserRole.ADMIN,
    }"
    :disable="{
      save: !canSave,
      inputs: {
        role: currentUser?.id === props.data.user.id, // Disable role input if the user is editing themselves
        email: currentUser?.id === props.data.user.id, // Disable email input if the user is editing themselves
      },
    }"
    :permission-modal="permissionModalData"
    @action="onAction"
  />
</template>
